import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { IProps } from "./select-similarity.types";
import { SIMILARITY_ITEMS, SimilarityValues } from "./select-similarity.constants";
import { useField } from "formik";
import { Text } from "../text";
import { Input } from "../input";
import { Icon } from "../icon";
import { Menu } from "../menu";
import styles from "./select-similarity.module.scss";
import { NumberInput } from "../number-input";

const SelectSimilarity: FunctionComponent<IProps> = ({
    name,
    onSelect,
    withinPortal,
    inputVariant,
    disabledItems = [],
}) => {
    const [fieldValues] = useField(name);
    const selectedValue = fieldValues.value;
    const [showCustomValue, setShowCustomValue] = useState(false);
    const [customValue, setCustomValue] = useState<string | number>(selectedValue * 100);
    const [menuOpened, setMenuOpened] = useState(false);

    useEffect(() => {
        setCustomValue(selectedValue ? selectedValue * 100 : "");
    }, [selectedValue]);

    const isCustomValueSelected = useMemo(() => {
        return customValue !== "" && Number(customValue) !== 0 && ![100, 95, 90, 85, 80].includes(Number(customValue));
    }, [customValue]);

    // Get the selected similarity value and convert to percentage
    const percentage = selectedValue ? Math.round(selectedValue * 100) : 100;

    const getSelectedLabel = (value: number) => {
        const percentageValue = value * 100;
        switch (true) {
            case percentageValue === 100:
                return "Exact Match";
            case percentageValue === 95:
                return "Close Match";
            case percentageValue === 90:
                return "Very Similar";
            case percentageValue === 85:
                return "Similar";
            case percentageValue === 80:
                return "Approximate";
            default:
                return "Custom";
        }
    };

    const getSelectedIcon = (value: number) => {
        const percentageValue = value * 100;

        if (percentageValue === 100) return SIMILARITY_ITEMS[0].icon;
        if (percentageValue === 95) return SIMILARITY_ITEMS[1].icon;
        if (percentageValue === 90) return SIMILARITY_ITEMS[2].icon;
        if (percentageValue === 85) return SIMILARITY_ITEMS[3].icon;
        if (percentageValue === 80) return SIMILARITY_ITEMS[4].icon;
        return SIMILARITY_ITEMS[5].icon;
    };

    return (
        <Menu
            withinPortal={withinPortal}
            opened={menuOpened}
            onChange={setMenuOpened}
            onClose={() => {
                setShowCustomValue(false);
            }}
        >
            <Menu.Target>
                <Input
                    component="button"
                    type="button"
                    variant={inputVariant}
                    leftSection={<Icon className={styles["icon"]} icon={getSelectedIcon(selectedValue || 1)} />}
                    rightSection={
                        <Text size="sm" mr={8} variant="secondary">
                            {percentage}%
                        </Text>
                    }
                >
                    {selectedValue ? getSelectedLabel(selectedValue) : "Exact Match"}
                </Input>
            </Menu.Target>
            <Menu.Dropdown miw={300}>
                <Menu.Info
                    title={"Word Similarity"}
                    subtitle="Shows interactions containing those specific keywords, or words similar to them in meaning and context."
                />
                <Menu.Divider />
                {SIMILARITY_ITEMS.map((item) => {
                    const currentPercentage = selectedValue ? Math.round(selectedValue * 100) : 100;

                    let isSelected = false;
                    if (item.value === SimilarityValues.CUSTOM) {
                        const predefinedValues = [100, 95, 90, 85, 80];
                        isSelected = selectedValue && !predefinedValues.includes(currentPercentage);
                    } else {
                        isSelected = selectedValue && currentPercentage === item.value;
                    }

                    return (
                        <Menu.Item
                            key={item.value}
                            active={isSelected}
                            disabled={disabledItems.includes(item.type)}
                            closeMenuOnClick={item.value !== SimilarityValues.CUSTOM}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (item.value === SimilarityValues.CUSTOM) {
                                    if (!showCustomValue || !isCustomValueSelected) {
                                        setShowCustomValue(true);
                                        setCustomValue("");
                                        // Don't close menu for custom
                                    }
                                } else {
                                    onSelect(item.value / 100);
                                    setShowCustomValue(false);
                                    setCustomValue("");
                                    setMenuOpened(false); // Close menu for non-custom items
                                }
                            }}
                            leftSection={<Icon className={styles["icon"]} icon={item.icon} />}
                            rightSection={
                                item.value !== SimilarityValues.CUSTOM ? (
                                    <Text size="sm" variant="secondary">
                                        {item.value}%
                                    </Text>
                                ) : undefined
                            }
                        >
                            {item.label}
                        </Menu.Item>
                    );
                })}
                {(showCustomValue || isCustomValueSelected) && (
                    <NumberInput
                        variant="filled"
                        placeholder="Enter a custom similarity value (80-100)"
                        value={customValue}
                        onChange={(value) => {
                            setCustomValue(Number(value));

                            const numValue = parseFloat(String(value));
                            if (!isNaN(numValue) && numValue > 80 && numValue <= 100) {
                                onSelect(numValue / 100);
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                const numValue = parseFloat(String(customValue));
                                if (!isNaN(numValue) && numValue > 80 && numValue <= 100) {
                                    onSelect(numValue / 100);
                                    setShowCustomValue(false);
                                    setMenuOpened(false);
                                }
                            }
                        }}
                        autoFocus
                    />
                )}
            </Menu.Dropdown>
        </Menu>
    );
};

export { SelectSimilarity };
